'use client';

import Image from 'next/image';
import dayjs from 'dayjs';
import Tag from '@/components/Tag';
import { useConfig } from '@/app/contexts/ConfigContext';
import CustomLink from './CustomLink';
import { ContentType, ServiceType } from '@/types';
import { CONTENT_MEDIA_TYPES, CONTENT_TEXT_TYPES } from '@/helpers/enums';
import { latoBold } from '@/helpers/fonts';
import WORDING from '@/wording';
import { shouldDisplayDate } from '@/helpers';

interface CardProps {
  timestamp: number;
  tags: string[];
  name: string;
  imageSrc: string;
  type: ContentType;
  link?: string;
  serviceType?: ServiceType;
}

export default function Card({
  tags,
  name,
  timestamp,
  imageSrc,
  type,
  link = '',
  serviceType,
}: CardProps) {
  const { wording, config, setConfig, updateWording, selectedLanguage } =
    useConfig();

  const handleConfig = (type?: ServiceType) => {
    if (type) {
      setConfig({
        ...config,
        serviceType: type,
      });
      updateWording(type, selectedLanguage);
    }
  };

  const handleTag = (tag: string) => {
    // Trick since we don't have Men/Women translation on Yoga/Pilates
    // ! This won't work for Love since we use this key for something else
    if (tag === 'Men') {
      return WORDING.fitness[selectedLanguage]!.text_media_1;
    }
    if (tag === 'Women') {
      return WORDING.fitness[selectedLanguage]!.text_media_2;
    }
    return tag;
  };

  return (
    <div
      className='sm:p-[38px] p-4 bg-white border border-neutral-300'
      style={{
        backgroundColor: config.theme.backgroundColor,
        borderColor: config.theme.borderColor,
      }}
    >
      <div className='relative'>
        <CustomLink
          href={link}
          onClick={() => {
            handleConfig(serviceType);
          }}
        >
          {(type === CONTENT_MEDIA_TYPES.EXERCISES ||
            type === CONTENT_MEDIA_TYPES.AUDIOS ||
            type === CONTENT_MEDIA_TYPES.VIDEOS) && (
            <svg
              width='70'
              height='70'
              viewBox='0 0 70 70'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className='absolute left-0 right-0 top-0 bottom-0 m-auto sm:h-[70px] h-[58px]'
            >
              <g id='Play Button'>
                <circle
                  id='Ellipse'
                  cx='35'
                  cy='35'
                  r='35'
                  fill={config.theme.playButtonCircle || '#7572F7'}
                />
                <path
                  id='Vector'
                  d='M30.7783 44.9415L42.218 34.8637L30.7783 24.7859V44.9415Z'
                  fill={config.theme.playButtonTriangle || 'white'}
                />
              </g>
            </svg>
          )}
          <Image
            width={388}
            height={218}
            className='w-auto'
            alt='image'
            src={imageSrc}
          />
        </CustomLink>
      </div>
      <div className='mt-5 flex flex-row flex-wrap gap-3'>
        {shouldDisplayDate(config) && (
          <div
            className={`${latoBold.className} text-secondary text-xl font-medium capitalize`}
            style={{ color: config.theme.accentColorTwo }}
          >
            {dayjs(timestamp).format('MMM DD, YYYY')}
          </div>
        )}

        {tags.map((tag) => (
          <Tag key={`${timestamp}-${tag}`} name={handleTag(tag)} />
        ))}
      </div>
      <div
        className='mt-5 text-neutral-800 sm:text-2xl text-xl font-bold'
        style={{ color: config.theme.textColorDark }}
      >
        {name}
      </div>
      <CustomLink
        href={link}
        onClick={() => {
          handleConfig(serviceType);
        }}
      >
        {(type === CONTENT_MEDIA_TYPES.EXERCISES ||
          type === CONTENT_MEDIA_TYPES.AUDIOS ||
          type === CONTENT_MEDIA_TYPES.VIDEOS) && (
          <div
            className={`${latoBold.className} mt-5 text-secondary text-xl font-medium`}
            style={{ color: config.theme.accentColorTwo }}
          >
            {wording?.components_card_link_media_content}
          </div>
        )}
        {(type === CONTENT_TEXT_TYPES.RECIPES ||
          type === CONTENT_TEXT_TYPES.GUIDES) && (
          <div
            className={`${latoBold.className} mt-5 text-secondary text-xl font-medium`}
            style={{ color: config.theme.accentColorTwo }}
          >
            {wording?.components_card_link_text1_content}
          </div>
        )}
        {type === CONTENT_TEXT_TYPES.ARTICLES && (
          <div
            className={`${latoBold.className} mt-5 text-secondary text-xl font-medium`}
            style={{ color: config.theme.accentColorTwo }}
          >
            {wording?.components_card_link_text2_content}
          </div>
        )}
      </CustomLink>
    </div>
  );
}
